import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Alert, Spinner, ListGroup, Badge } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faUserPlus, faTimes } from '@fortawesome/free-solid-svg-icons';
import { backendUrl } from '../utils/variables';

const InvitePartnerModal = ({ show, onClose, goal, user }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);

  useEffect(() => {
    if (!searchQuery || searchQuery.length < 2) {
      setSearchResults([]);
      setError(null);
      return;
    }

    if (!user || !user.user_id) {
      setError('User information not available');
      return;
    }

    const searchUsers = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const response = await fetch(`${backendUrl}/search_partner.php`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ 
            query: searchQuery,
            exclude_id: user.user_id // Exclude current user from results
          })
        });
        
        const data = await response.json();
        console.log('Search results:', data);
        if (data.success) {
          setSearchResults(data.users || []);
          setError(null);
        } else {
          setError(data.message || 'Failed to search users');
          setSearchResults([]);
        }
      } catch (err) {
        setError('Network error: ' + err.message);
        setSearchResults([]);
        console.error('Search error:', err);
      } finally {
        setLoading(false);
      }
    };

    // Add debounce to prevent too many requests
    const timer = setTimeout(searchUsers, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, user?.user_id]);

  const handleInvite = async () => {
    if (!selectedUser || !goal) return;

    try {
      setLoading(true);
      setError(null);
      setSuccess(null);
      
      const response = await fetch(`${backendUrl}/invite_partner.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          goal_id: goal.id,
          partner_id: selectedUser.id,
          user_id: user.user_id
        })
      });
      
      const data = await response.json();
      if (data.success) {
        setSuccess(`Invitation sent to ${selectedUser.name || selectedUser.username}`);
        setSelectedUser(null);
        setSearchQuery('');
        setSearchResults([]);
      } else {
        setError(data.messages || 'Failed to send invitation');
      }
    } catch (err) {
      setError('Network error: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    onClose();
    setSearchQuery('');
    setSearchResults([]);
    setSelectedUser(null);
    setError(null);
    setSuccess(null);
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          <FontAwesomeIcon icon={faUserPlus} className="me-2" />
          Invite Partner to "{goal?.title || 'Goal'}"
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && (
          <Alert variant="danger" dismissible onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert variant="success" dismissible onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}
        
        <Form.Group className="mb-3">
          <Form.Label>Search for users to invite</Form.Label>
          <div className="d-flex">
            <Form.Control
              type="text"
              placeholder="Search by username..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <Button variant="outline-secondary" disabled={loading}>
              <FontAwesomeIcon icon={faSearch} />
            </Button>
          </div>
          <Form.Text className="text-muted">
            Enter at least 2 characters to search
          </Form.Text>
        </Form.Group>

        {selectedUser && (
          <div className="mb-3 p-3 bg-light rounded">
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <strong>Selected Partner:</strong> {selectedUser.name || selectedUser.username}
              </div>
              <Button 
                variant="outline-danger" 
                size="sm"
                onClick={() => setSelectedUser(null)}
              >
                <FontAwesomeIcon icon={faTimes} />
              </Button>
            </div>
          </div>
        )}

        {loading && searchQuery.length > 0 && (
          <div className="text-center py-3">
            <Spinner animation="border" variant="primary" />
          </div>
        )}

        {!loading && searchResults.length > 0 && !selectedUser && (
          <div className="search-results-container" style={{ maxHeight: '300px', overflowY: 'auto' }}>
            <ListGroup>
              {searchResults.map(user => (
                <ListGroup.Item 
                  key={user.id} 
                  action 
                  onClick={() => setSelectedUser(user)}
                  className="d-flex justify-content-between align-items-center"
                >
                  <div>
                    <div>{user.name || user.username}</div>
                    <small className="text-muted">@{user.username}</small>
                  </div>
               
                </ListGroup.Item>
              ))}
            </ListGroup>
          </div>
        )}

        {!loading && searchQuery.length > 1 && searchResults.length === 0 && (
          <div className="text-center py-3 text-muted">
            No users found matching "{searchQuery}"
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          Cancel
        </Button>
        <Button 
          variant="primary" 
          onClick={handleInvite}
          disabled={!selectedUser || loading}
        >
          {loading ? (
            <>
              <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
              <span className="ms-2">Sending...</span>
            </>
          ) : (
            'Send Invitation'
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default InvitePartnerModal;